/**
 * Callout plugin — GitHub alerts + Obsidian-style aliases on top of
 * blockquotes. Both ecosystems use the `> [!KIND]` marker on the first
 * line of a blockquote; we treat them as one syntax and emit the same
 * `<aside data-callout="<canonical>">` markup either way so the .prose
 * stylesheet only has to skin five callout types, not eleven.
 *
 *   > [!NOTE]                    <aside data-callout="note">
 *   > body text                    body text
 *                                </aside>
 *
 * Implementation lives in `core.ruler.after('block', ...)` — markdown-it's
 * block ruler has already produced the `blockquote_open` / `paragraph_open`
 * / `inline` / `paragraph_close` / `blockquote_close` token stream, so we
 * walk that stream once. For each blockquote whose first paragraph's first
 * inline line matches the marker pattern, we:
 *
 *   1. Map the marker key through ALIASES to a canonical kind (or skip if
 *      no mapping exists; an unknown `[!FOO]` blockquote renders as a
 *      normal blockquote, matching GitHub's behaviour).
 *   2. Mutate `blockquote_open.tag` to "aside", stamp `data-callout` attr.
 *   3. Find the matching `blockquote_close` (depth-tracked) and switch its
 *      tag to "aside" so the closing element matches.
 *   4. Strip the marker line from the inline token's `.content` and re-run
 *      `md.parseInline` on the remainder so the inline children reflect
 *      the cleaned text (otherwise the rendered `<aside>` would still show
 *      the literal "[!NOTE]" line above the body).
 *
 * `line.match(re)` over `re.test/...` per repo lint convention — same
 * shape for the non-global regex, easier to reason about.
 */

import type MarkdownIt from 'markdown-it'

export type CalloutKind = 'note' | 'tip' | 'important' | 'warning' | 'caution'

// Map raw marker keys (case-insensitive) to canonical kinds. Covers both the
// official GitHub set and the Obsidian aliases the spec calls out.
const ALIASES: Record<string, CalloutKind> = {
  // GitHub canonical
  note: 'note',
  tip: 'tip',
  important: 'important',
  warning: 'warning',
  caution: 'caution',
  // Obsidian aliases
  info: 'note',
  hint: 'tip',
  success: 'tip',
  quote: 'important',
  attention: 'warning',
  danger: 'caution',
  error: 'caution',
}

const MARKER_RE = /^\s*\[!(\w+)\]\s*$/

const KIND_LABELS: Record<CalloutKind, string> = {
  note: 'Note',
  tip: 'Tip',
  important: 'Important',
  warning: 'Warning',
  caution: 'Caution',
}

export function calloutPlugin(md: MarkdownIt): void {
  const defaultOpen =
    md.renderer.rules.blockquote_open ??
    ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options))

  md.renderer.rules.blockquote_open = (tokens, idx, options, env, self) => {
    const token = tokens[idx]
    if (token?.tag === 'aside') {
      const kind = (token.attrGet('data-callout') ?? 'note') as CalloutKind
      const label = KIND_LABELS[kind] ?? kind
      return `<aside${self.renderAttrs(token)}><span class="callout-label">${label}</span>`
    }
    return defaultOpen(tokens, idx, options, env, self)
  }

  md.core.ruler.after('block', 'callout', (state) => {
    const tokens = state.tokens
    for (let i = 0; i < tokens.length; i++) {
      const open = tokens[i]
      if (!open || open.type !== 'blockquote_open') continue

      // Expect `paragraph_open` immediately, then `inline` whose content
      // first line carries the marker. If the surrounding shape is
      // anything else (loose list inside the quote, raw HTML, etc.) we
      // just leave the blockquote alone.
      const paraOpen = tokens[i + 1]
      const inline = tokens[i + 2]
      if (!paraOpen || paraOpen.type !== 'paragraph_open') continue
      if (!inline || inline.type !== 'inline') continue

      const firstLineEnd = inline.content.indexOf('\n')
      const firstLine = firstLineEnd === -1 ? inline.content : inline.content.slice(0, firstLineEnd)
      const match = firstLine.match(MARKER_RE)
      if (!match) continue

      const rawKey = match[1]?.toLowerCase() ?? ''
      const kind = ALIASES[rawKey]
      if (!kind) continue

      // Mutate the opening tag to aside, with the canonical data-callout
      // attribute. markdown-it's Token API exposes attrSet which dedupes
      // by key — safe to call even if a future plugin already set one.
      open.tag = 'aside'
      open.attrSet('data-callout', kind)

      // Match the corresponding blockquote_close. Track nesting depth in
      // case the blockquote contains nested blockquotes (rare but legal).
      let depth = 1
      let closeIndex = -1
      for (let j = i + 1; j < tokens.length; j++) {
        const t = tokens[j]
        if (!t) continue
        if (t.type === 'blockquote_open') depth++
        else if (t.type === 'blockquote_close') {
          depth--
          if (depth === 0) {
            closeIndex = j
            break
          }
        }
      }
      if (closeIndex !== -1) {
        const close = tokens[closeIndex]
        if (close) close.tag = 'aside'
      }

      // Strip the marker line from the inline content. The remainder
      // may be empty (marker-only blockquote → empty aside body) — that
      // still renders a styled empty aside, which is the desired shape.
      //
      // markdown-it's renderer prefers `children` over `content` for inline
      // tokens, but some token types still serialize `content` directly —
      // setting BOTH duplicated the callout body in the rendered output
      // (visible as "Use ## for sub-headings.Use ## for sub-headings."
      // in the WELCOME sample). Set `content` to empty after re-parsing
      // so the children tree is the only source.
      const rest = firstLineEnd === -1 ? '' : inline.content.slice(firstLineEnd + 1)
      inline.children = md.parseInline(rest, state.env)[0]?.children ?? []
      inline.content = ''
    }
  })
}
